'use client';

import React, { memo, useMemo } from 'react';

import { CustomProgress } from '@repo/ui/components/ui/progress';

export const ARTICLE_FACTS_COLORS = {
  ['0-40']: { start: 'rgb(220, 38, 38)', end: 'rgb(252, 165, 165)' },
  ['41-60']: { start: 'rgb(255, 165, 0)', end: 'rgb(253, 224, 71)' },
  ['61-75']: { start: 'rgb(91, 180, 91)', end: 'rgb(134, 239, 172)' },
  ['75-100']: { start: 'rgb(0, 128, 0)', end: 'rgb(74, 222, 128)' },
} as const;

// MISCOMPILATION GUARD (same as lib/metric-utils.ts): the Turbopack prod
// minifier merges adjacent else-if branches that return different hoisted
// map members (`e>=41||e>=21 ? orange : green` shipped in this exact file,
// deleting the 21-40/41-60 mid-ranges). Resolve a distinct range key per
// branch and index the map once — do not inline the returns again.
export const getArticleFactsColor = (percentage?: number) => {
  const percent = Math.round(percentage ?? 0);
  let range: keyof typeof ARTICLE_FACTS_COLORS;
  if (percent >= 75) range = '75-100';
  else if (percent >= 61) range = '61-75';
  else if (percent >= 41) range = '41-60';
  else range = '0-40';
  return ARTICLE_FACTS_COLORS[range];
};

export const ARTICLE_BIAS_COLORS = {
  ['0-20']: { start: 'rgb(0, 128, 0)', end: 'rgb(74, 222, 128)' },
  ['21-40']: { start: 'rgb(91, 180, 91)', end: 'rgb(134, 239, 172)' },
  ['41-65']: { start: 'rgb(255, 165, 0)', end: 'rgb(253, 224, 71)' },
  ['66-100']: { start: 'rgb(220, 38, 38)', end: 'rgb(252, 165, 165)' },
} as const;

export const getArticleBiasColor = (percentage?: number) => {
  const percent = Math.round(percentage ?? 0);
  let range: keyof typeof ARTICLE_BIAS_COLORS;
  if (percent >= 66) range = '66-100';
  else if (percent >= 41) range = '41-65';
  else if (percent >= 21) range = '21-40';
  else range = '0-20';
  return ARTICLE_BIAS_COLORS[range];
};

interface ArticleCardMetricsProgressBarsProps {
  claimedFactsScores?: number;
  biasScore?: number;
}

const PureArticleCardMetricsProgressBars: React.FC<
  ArticleCardMetricsProgressBarsProps
> = ({ claimedFactsScores, biasScore }) => {
  const factsPercentColor = useMemo(
    () => getArticleFactsColor(claimedFactsScores),
    [claimedFactsScores],
  );

  const biasPercentColor = useMemo(
    () => getArticleBiasColor(biasScore),
    [biasScore],
  );

  return (
    <div className="flex w-full items-center gap-4">
      <div className="w-full flex items-center gap-2">
        <span className="font-medium text-black text-[13px] leading-[14px] cred-lg:text-sm cred-lg:leading-[15px] cred-xl:text-base cred-xl:leading-[19px]">
          Facts
        </span>
        <div className="w-full relative">
          <CustomProgress
            direction="left-to-right"
            start={0}
            end={100}
            value={claimedFactsScores ?? 0}
            className="h-[18px] w-full bg-[#EBEBEE] cred-xl:h-[21px] [&>div]:bg-[linear-gradient(to_right,var(--progress-color-start)_25%,var(--progress-color-end)_100%)] [&>div]:min-w-9 rounded-none [&>div]:rounded-r-md"
            style={
              {
                '--progress-color-start': factsPercentColor.start,
                '--progress-color-end': factsPercentColor.end,
              } as React.CSSProperties
            }
          />
          <div className="absolute inset-y-0 inset-x-1.5 flex items-center justify-start lg:inset-x-2.5">
            <span className="block font-bold text-white text-xs leading-[14px] cred-lg:text-[13px] cred-lg:leading-[15px] cred-xl:text-base cred-xl:leading-[19px]">
              {(claimedFactsScores ?? 0).toFixed(0)}%
            </span>
          </div>
        </div>
      </div>
      <div className="w-full flex items-center gap-2">
        <span className="font-medium text-black text-[13px] leading-[14px] cred-lg:text-sm cred-lg:leading-[15px] cred-xl:text-base cred-xl:leading-[19px]">
          Bias
        </span>
        <div className="w-full relative">
          <CustomProgress
            direction="left-to-right"
            start={0}
            end={100}
            value={biasScore ?? 0}
            className="h-[18px] w-full bg-[#EBEBEE] cred-xl:h-[21px] [&>div]:bg-[linear-gradient(to_right,var(--progress-color-start)_25%,var(--progress-color-end)_100%)] [&>div]:min-w-9 rounded-none [&>div]:rounded-r-md"
            style={
              {
                '--progress-color-start': biasPercentColor.start,
                '--progress-color-end': biasPercentColor.end,
              } as React.CSSProperties
            }
          />
          <div className="absolute inset-y-0 inset-x-1.5 flex items-center justify-start lg:inset-x-2.5">
            <span className="block font-bold text-white text-xs leading-[14px] cred-lg:text-[13px] cred-lg:leading-[15px] cred-xl:text-base cred-xl:leading-[19px]">
              {(biasScore ?? 0).toFixed(0)}%
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
export const ArticleCardMetricsProgressBars = memo(
  PureArticleCardMetricsProgressBars,
  (prevProps, nextProps) => {
    return (
      prevProps.claimedFactsScores === nextProps.claimedFactsScores &&
      prevProps.biasScore === nextProps.biasScore
    );
  },
);
